'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';

import Footer from '@/components/Footer';

import FisabilillahFeed from './components/FisabilillahFeed';
import CollaborationSpace from './components/CollaborationSpace';


// ─── TYPES ────────────────────────────────────────────────────────────────────

interface RolloutUpdate {
  id: string;
  title: string;
  description: string;
  status: 'live' | 'building' | 'upcoming';
  category: 'feature' | 'content' | 'community' | 'event';
  created_at: string;
}

interface SignupForm {
  name: string;
  email: string;
  role: string;
  city: string;
  source: string;
}

interface SpotlightStory {
  id: string;
  role: string;
  city: string;
  quote: string;
  resonance_thread: string;
  anonymous: boolean;
  name?: string;
  created_at: string;
}

// ─── STATIC SPOTLIGHT DATA ────────────────────────────────────────────────────

const SPOTLIGHT_STORIES: SpotlightStory[] = [
  {
    id: 's1',
    role: 'community-caretaker',
    city: 'Atlanta',
    quote: 'The MRG helped me see the gaps in our neighborhood as Amanah — not problems to fix, but trusts to fulfill. I started mapping what we have before asking what we lack.',
    resonance_thread: 'MRG · Justice · Al-Adl',
    anonymous: false,
    name: 'Sister Amira',
    created_at: '2026-01-15',
  },
  {
    id: 's2',
    role: 'inner-seeker',
    city: 'Chicago',
    quote: 'The IRL Lens changed how I walk through my city. Every tree, every crack in the sidewalk — I started seeing Ayat. The Leaf Sequence gave me language for what I was already experiencing.',
    resonance_thread: 'IRL Lens · Ayat · Al-Zahir',
    anonymous: true,
    created_at: '2026-01-22',
  },
  {
    id: 's3',
    role: 'academic',
    city: 'Durham',
    quote: 'The Names Reference is the most rigorous Islamic ontological framework I have encountered outside of classical texts. The cross-linking to IRL observations is genuinely novel scholarship.',
    resonance_thread: 'Names · Keys · Al-Alim',
    anonymous: false,
    name: 'Dr. Yusuf',
    created_at: '2026-02-03',
  },
  {
    id: 's4',
    role: 'community-caretaker',
    city: 'Detroit',
    quote: 'I used the justice-involved resources section to help three brothers from my masjid find housing after release. The Mizan framing helped them see their situation differently — not as punishment, but as a test of Amanah.',
    resonance_thread: 'MRG · Justice · Mizan',
    anonymous: true,
    created_at: '2026-02-10',
  },
  {
    id: 's5',
    role: 'inner-seeker',
    city: 'Portland',
    quote: 'I completed the full walkthrough three times. Each time I noticed something different. The Recognition Arc is real — I am genuinely in a different place than when I started.',
    resonance_thread: 'IRL Lens · Walkthrough · Recognition',
    anonymous: false,
    name: 'Brother Tariq',
    created_at: '2026-02-18',
  },
  {
    id: 's6',
    role: 'activist',
    city: 'Houston',
    quote: 'Connecting environmental racism to Al-Fasad and Al-Adl gave our organizing work a theological grounding that resonated deeply with our community. The framework is not abstract — it is actionable.',
    resonance_thread: 'Justice · IRL · Al-Fasad',
    anonymous: false,
    name: 'Ustadha Khadija',
    created_at: '2026-02-25',
  },
];

const SHARED_RESONANCE_PATHWAYS = [
  {
    id: 'p1',
    title: 'The Recognition Arc',
    arabic: 'قوس الاعتراف',
    icon: '◎',
    nodes: ['Mata', 'Encounter', 'Examination', 'Recognition', 'Re-Assembly', 'Uboor'],
    color: '#c9a84c',
    count: 847,
    description: 'The most-traveled pathway — from first encounter to crossing. Shared by seekers across all roles.',
  },
  {
    id: 'p2',
    title: 'The Justice Thread',
    arabic: 'خيط العدالة',
    icon: '⬡',
    nodes: ['Al-Adl', 'Al-Haqq', 'Mizan', 'MRG', 'Community Action'],
    color: '#2d7a47',
    count: 312,
    description: 'Community caretakers and activists converge here — Names of justice linked to real resources.',
  },
  {
    id: 'p3',
    title: 'The Scholarship Path',
    arabic: 'مسار العلم',
    icon: '✦',
    nodes: ['Names Reference', 'Keys', 'SL Glossary', 'Ayat', 'Open Letter'],
    color: '#d4a82a',
    count: 203,
    description: 'Academics and deep seekers — from ontological vocabulary to applied framework.',
  },
  {
    id: 'p4',
    title: 'The Ecological Lens',
    arabic: 'العدسة البيئية',
    icon: '☽',
    nodes: ['IRL Lens', 'Leaf Sequence', 'Ayat in Creation', 'Mycorrhizal', 'Uboor'],
    color: '#2d8a87',
    count: 519,
    description: 'The creation-observation pathway — from physical encounter to recognition of Ayat.',
  },
];

const ROLE_COLORS: Record<string, string> = {
  'inner-seeker': '#8a8ab8',
  'community-caretaker': '#2d7a47',
  'academic': '#d4a82a',
  'activist': '#2d8a87',
  'explorer': '#c9a84c',
};

const SPOTLIGHT_ROLE_FILTERS = [
  { value: 'all', label: 'All Roles' },
  { value: 'community-caretaker', label: 'Community Caretaker' },
  { value: 'inner-seeker', label: 'Inner Seeker' },
  { value: 'academic', label: 'Academic' },
  { value: 'activist', label: 'Activist' },
];

const ROLES = ['Inner Seeker', 'Community Caretaker', 'Academic', 'Activist', 'Explorer'];
const SOURCES = ['Social Media', 'Friend', 'Search', 'Masjid', 'Other'];

// ─── TEMPLATE GENERATOR ───────────────────────────────────────────────────────

const SL_TERMS = [
  { term: 'Islah', arabic: 'إِصْلَاح', meaning: 'reform, setting right', context: 'Use when describing community restoration work' },
  { term: 'Amanah', arabic: 'أَمَانَة', meaning: 'sacred trust', context: 'Use when framing responsibility and stewardship' },
  { term: 'Fisabilillah', arabic: 'فِي سَبِيلِ اللَّه', meaning: 'in the path of Allah', context: 'Use when orienting service and giving' },
  { term: 'Khidmah', arabic: 'خِدْمَة', meaning: 'service, caretaking', context: 'Use when describing acts of service' },
  { term: 'Mizan', arabic: 'مِيزَان', meaning: 'balance, divine scale', context: 'Use when addressing justice and equity' },
  { term: 'Haqq', arabic: 'حَقّ', meaning: 'truth, right, due', context: 'Use when asserting rights or truth' },
  { term: 'Falah', arabic: 'فَلَاح', meaning: 'flourishing, cultivation', context: 'Use when reframing success' },
  { term: 'Taqwah', arabic: 'تَقْوَى', meaning: 'God-consciousness', context: 'Use when grounding action in awareness' },
];

const TEMPLATE_TYPES = [
  { id: 'email', label: 'Email', icon: '✉' },
  { id: 'social', label: 'Social Post', icon: '◎' },
  { id: 'inperson', label: 'In-Person Script', icon: '⬡' },
  { id: 'fundraising', label: 'Fundraising Ask', icon: '✦' },
];

const TEMPLATE_PHASES = [
  { id: 'awareness', label: 'Awareness', desc: 'Introducing the framework to new caretakers' },
  { id: 'invitation', label: 'Invitation', desc: 'Inviting community members to participate' },
  { id: 'action', label: 'Action', desc: 'Mobilizing for specific acts of Khidmah' },
  { id: 'reflection', label: 'Reflection', desc: 'Post-event or post-action sharing' },
];

const TEMPLATES: Record<string, Record<string, string>> = {
  email: {
    awareness: `Bismillah,

As-salamu alaykum wa rahmatullahi wa barakatuh,

I wanted to share something that has been reshaping how I see our work together.

The Haal Project is a framework for reading creation as Ayat — signs of Allah — and connecting that recognition to our Amanah as caretakers of this community. It is not a program. It is a methodology for seeing differently.

The framework is built on a simple premise: every condition we encounter — every Haal — is addressed by the Names and Attributes of Allah. When we see a vacant lot, we can see Al-Muhyi waiting to be recognized. When we see a family without housing, we can see the Amanah we carry as Khalifa.

I invite you to explore: haalproject.org

Fisabilillah,
[Your Name]`,
    invitation: `Bismillah,

As-salamu alaykum,

You are invited to a gathering of caretakers — people who see their work as Khidmah, as service in the path of Allah.

We will be walking through the IRL Lens together: a structured practice of reading the signs of Allah in our neighborhood, our community, and ourselves. We will ask three questions of what we encounter:

1. IS IT? — confirming existence, recognizing Wujud
2. WHAT IS IT? — examining essence, reading Mahiyya  
3. WHEN IS/WAS IT? — tracing sequence, recognizing Sunnat Allah

This is not a lecture. It is a practice. Bring your curiosity and your Taqwah.

Date: [Date]
Location: [Location]

Fisabilillah 🌿

#Khidmah #IRL #HaalProject`,
    action: `Bismillah,

As-salamu alaykum,

This is a call to Islah — to the work of setting things right.

[Describe the specific need or situation]

This is not charity. This is Amanah — the trust we carry as caretakers of this community. The Quran tells us: "We offered the Amanah to the heavens and the earth and the mountains, and they declined to bear it — but the human being accepted it." (33:72)

We accepted it. Now we fulfill it.

How to participate: [Specific action]

Fisabilillah — in the path of Allah,
[Your Name]`,
    reflection: `Bismillah,

Alhamdulillah.

We gathered. We served. We saw Ayat in the work.

[Describe what happened]

What struck me most was [specific observation]. In the framework of the IRL Lens, this is what we call Uboor — the crossing. The moment when recognition becomes action, when seeing becomes being.

The Mizan was present in [specific example]. Al-Adl was visible in [specific example].

Jazakum Allahu khayran to everyone who showed up. The Amanah is lighter when we carry it together.

Fisabilillah,
[Your Name]`,
  },
  social: {
    awareness: `The Haal Project is not an app.

It is a methodology for seeing creation differently — rooted in the Names and Attributes of Allah.

Every leaf, every community, every condition (Haal) is an Ayah — a sign pointing back to its Creator.

The IRL Lens gives that recognition a structure. The Keys give it a vocabulary. The MRG gives it a direction.

haalproject.org

#HaalProject #Amanah #Taqwah #Fisabilillah`,
    invitation: `You are invited.

Not to a program. Not to a lecture.

To a practice of seeing — of reading the Ayat of Allah in the world around you.

The IRL Lens. Three questions. One recognition arc.

[Date · Location]

Fisabilillah 🌿

#Khidmah #IRL #HaalProject`,
    action: `This is Islah.

Not reform as a political project — but Islah as the Quran means it: setting things right.

[Describe the action]

The Mizan demands it. The Amanah requires it.

Join us: [Link or location]

#Fisabilillah #Islah #Amanah #HaalProject`,
    reflection: `Alhamdulillah.

We showed up. We served. We saw Ayat in the work.

[What happened]

This is what Falah looks like — not achievement, but cultivation. Patient, rooted, generational.

Hayya ala al-Falah. 🌱

#Alhamdulillah #Falah #HaalProject #Fisabilillah`,
  },
  inperson: `Bismillah.

[Opening — use this when introducing the framework to a new group]

"As-salamu alaykum. I want to start with a question: What is the condition of this community right now? What is its Haal?

The word Haal — حال — means state of being. The Haal Project takes its name from the phrase 'Kulli Haal' — every state. The idea is that every condition, every situation, every Haal we encounter is addressed by the Names and Attributes of Allah.

We are not here to fix problems. We are here to recognize Amanah — the trust we carry as caretakers of this community. And then to act from that recognition.

Let me show you the IRL Lens — a tool for reading what you already see..."

[Transition to the Three Questions exercise]`,
  fundraising: `Bismillah,

As-salamu alaykum,

I am writing to you as a fellow caretaker — someone who sees their work as Amanah, as a sacred trust.

The Haal Project is a platform for caretakers: people who tend to communities, ecosystems, and the conditions of those around them. It is built on the recognition that every act of Khidmah — every act of service in the path of Allah — is an act of Islah: setting things right.

We are asking for your support Fisabilillah — in the path of Allah — to [specific need].

The Quran tells us: "Indeed, Allah commands justice and Ihsan." (16:90) This is our Ihsan — our excellence in service, as if we see Allah.

Your contribution of [amount] will [specific impact].

Jazakum Allahu khayran,
[Your Name]`,
};

function TemplateGenerator() {
  const [selectedType, setSelectedType] = useState('email');
  const [selectedPhase, setSelectedPhase] = useState('awareness');
  const [copied, setCopied] = useState(false);
  const [customizations, setCustomizations] = useState({ name: '', city: '', org: '', action: '' });

  const getTemplate = () => {
    const base = TEMPLATES[selectedType]?.[selectedPhase] || TEMPLATES[selectedType] || '';
    if (typeof base !== 'string') return '';
    return base
      .replace('[Your Name]', customizations.name || '[Your Name]')
      .replace('[City]', customizations.city || '[City]')
      .replace('[Organization]', customizations.org || '[Organization]')
      .replace('[Specific action]', customizations.action || '[Specific action]');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getTemplate()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const template = getTemplate();

  return (
    <div className="space-y-6">
      {/* Type selector */}
      <div>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>FORMAT</p>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedType(t.id)}
              className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all duration-200"
              style={{
                background: selectedType === t.id ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selectedType === t.id ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)'}`,
                color: selectedType === t.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Phase selector (only for email/social) */}
      {(selectedType === 'email' || selectedType === 'social') && (
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>PHASE</p>
          <div className="grid grid-cols-2 gap-2">
            {TEMPLATE_PHASES.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedPhase(p.id)}
                className="text-left rounded-lg p-3 transition-all duration-200"
                style={{
                  background: selectedPhase === p.id ? 'rgba(45,138,135,0.1)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${selectedPhase === p.id ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: selectedPhase === p.id ? '#2d8a87' : 'rgba(212,197,169,0.5)' }}>{p.label}</p>
                <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 10, marginTop: 2 }}>{p.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Customization fields */}
      <div>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>PERSONALIZE (OPTIONAL)</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'name', placeholder: 'Your name' },
            { key: 'city', placeholder: 'Your city' },
            { key: 'org', placeholder: 'Your organization' },
            { key: 'action', placeholder: 'Specific action/event' },
          ].map(f => (
            <input
              key={f.key}
              type="text"
              placeholder={f.placeholder}
              value={customizations[f.key as keyof typeof customizations]}
              onChange={e => setCustomizations(c => ({ ...c, [f.key]: e.target.value }))}
              className="px-3 py-2 font-mono text-xs focus:outline-none rounded"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.12)', color: '#d4c5a9' }}
            />
          ))}
        </div>
      </div>

      {/* Generated template */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.5)' }}>GENERATED COPY</p>
          <button
            onClick={handleCopy}
            className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{
              background: copied ? 'rgba(45,122,71,0.15)' : 'rgba(201,168,76,0.08)',
              border: `1px solid ${copied ? 'rgba(45,122,71,0.4)' : 'rgba(201,168,76,0.25)'}`,
              color: copied ? '#2d7a47' : '#c9a84c',
            }}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <pre
          className="whitespace-pre-wrap rounded-xl p-5 text-xs leading-relaxed overflow-y-auto"
          style={{
            background: 'rgba(6,13,18,0.8)',
            border: '1px solid rgba(201,168,76,0.1)',
            color: 'rgba(212,197,169,0.8)',
            fontFamily: 'monospace',
            fontSize: 11,
            maxHeight: 320,
          }}
        >
          {template}
        </pre>
      </div>

      {/* SL Terms reference */}
      <div>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>SACRED LANGUAGE TERMS IN THIS TEMPLATE</p>
        <div className="flex flex-wrap gap-2">
          {SL_TERMS.filter(t => template.includes(t.term)).map(t => (
            <div key={t.term} className="rounded px-3 py-1.5" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: '#c9a84c' }}>{t.term}</span>
              <span style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, marginLeft: 6 }}>— {t.meaning}</span>
            </div>
          ))}
        </div>
        <Link href="/sl-glossary" className="font-mono text-[9px] uppercase tracking-widest mt-3 inline-block" style={{ color: 'rgba(45,138,135,0.6)' }}>
          → Full SL Glossary
        </Link>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function CommunityPage() {
  const [activePathway, setActivePathway] = useState<string>('');
  const [activeSpotlightRole, setActiveSpotlightRole] = useState<string>('all');
  const [userRole, setUserRole] = useState<string>('explorer');
  const [rolloutUpdates, setRolloutUpdates] = useState<RolloutUpdate[]>([]);
  const [signupSubmitted, setSignupSubmitted] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [form, setForm] = useState<SignupForm>({ name: '', email: '', role: '', city: '', source: '' });
  const signupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedRole = localStorage.getItem('haal_role') || 'explorer';
      setUserRole(savedRole);
    }
  }, []);

  const filteredSpotlights = SPOTLIGHT_STORIES.filter(
    s => activeSpotlightRole === 'all' || s.role === activeSpotlightRole
  );

  const scrollToSignup = useCallback(() => {
    signupRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupLoading(true);
    setSignupError('');
    setTimeout(() => {
      setSignupLoading(false);
      setSignupSubmitted(true);
    }, 1000);
  };

  return (
    <div className="min-h-screen" style={{ background: '#f5f0e8' }}>

      {/* ── QURAN 49:13 EPIGRAPH ── */}
      <section style={{ background: '#060d12', borderBottom: '1px solid rgba(201,168,76,0.1)', padding: '48px 24px' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: 'rgba(201,168,76,0.6)', direction: 'rtl', marginBottom: 20, lineHeight: 1.8 }}>
            يَا أَيُّهَا النَّاسُ إِنَّا خَلَقْنَاكُم مِّن ذَكَرٍ وَأُنثَىٰ وَجَعَلْنَاكُمْ شُعُوبًا وَقَبَائِلَ لِتَعَارَفُوا
          </p>
          <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 15, lineHeight: 1.9, fontStyle: 'italic', marginBottom: 12 }}>
            "O Mankind, indeed We have created you from a male and a female and made you nations and tribes that you may know one another. Indeed, the most noble of you in the sight of Allah is the most righteous of you. Indeed, Allah is All-Knowing, All-Aware."
          </p>
          <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.4)' }}>
            Quran 49:13 · Al-Hujurat · The Rooms
          </p>
        </div>
      </section>

      {/* ── FISABILILLAH FEED — PRIMARY COMPONENT ── */}
      <FisabilillahFeed />

      {/* ── COLLABORATION SPACE ── */}
      <CollaborationSpace />

      {/* ── IN-PERSON CARETAKER KITS — BELOW THE FEED ── */}
      <section className="py-20 px-6" style={{ background: '#0a1a12' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>
              مجموعات القائمين · THE PHYSICAL BRIDGE
            </p>
            <h2 className="font-mono uppercase tracking-widest mb-4" style={{ color: '#c9a84c', fontSize: 'clamp(14px, 3vw, 22px)', letterSpacing: '0.2em' }}>
              IN-PERSON CARETAKER KITS
            </h2>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, fontStyle: 'italic', maxWidth: 560, margin: '0 auto' }}>
              The platform is distributable. These kits bring the framework into masjids, community centers, classrooms, and prisons — the physical world where the Amanah is fulfilled.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Community Introduction Kit */}
            <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.25)', borderTop: '3px solid #c9a84c' }}>
              <div className="px-6 py-5" style={{ borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <span style={{ color: '#c9a84c', fontSize: 20 }}>⬡</span>
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.5)' }}>FOR COMMUNITY EVENTS & MASJIDS</p>
                    <h3 className="font-mono uppercase tracking-wide" style={{ color: '#c9a84c', fontSize: 13 }}>Community Introduction Kit</h3>
                  </div>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>
                  Everything needed to introduce the Haal Project framework to a new community — from a single conversation to a full gathering.
                </p>
              </div>
              <div className="px-6 py-5">
                <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>INCLUDES</p>
                <ul className="space-y-2 mb-6">
                  {['Platform overview card', 'QR code to /guided-intro', '5 Haals explanation card', 'IRL Lens quick-start guide', 'MRG resource list (city-specific)', '"Kaif Haal?" conversation starter cards'].map(item => (
                    <li key={item} className="flex items-start gap-2" style={{ color: '#4a5a52', fontSize: 12 }}>
                      <span style={{ color: '#c9a84c', flexShrink: 0 }}>·</span>{item}
                    </li>
                  ))}
                </ul>
                <a
                  href="mailto:contact@haalproject.org?subject=Community Introduction Kit Request"
                  className="font-mono uppercase tracking-widest px-6 py-3 inline-block transition-all duration-200"
                  style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', fontSize: 10, letterSpacing: '0.15em' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(201,168,76,0.08)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
                >
                  Request a Kit →
                </a>
              </div>
            </div>

            {/* Educator Curriculum Kit */}
            <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.25)', borderTop: '3px solid #2d7a47' }}>
              <div className="px-6 py-5" style={{ borderBottom: '1px solid rgba(45,122,71,0.1)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <span style={{ color: '#2d7a47', fontSize: 20 }}>◈</span>
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(45,122,71,0.5)' }}>FOR CLASSROOMS & EDUCATIONAL SETTINGS</p>
                    <h3 className="font-mono uppercase tracking-wide" style={{ color: '#2d7a47', fontSize: 13 }}>Educator Curriculum Kit</h3>
                  </div>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>
                  A structured curriculum companion for educators bringing the Keys framework into formal learning environments.
                </p>
              </div>
              <div className="px-6 py-5">
                <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>INCLUDES</p>
                <ul className="space-y-2 mb-6">
                  {['Keys to Kulli Haal curriculum overview', 'Sequence cards (S4-S7)', 'Sacred Language glossary card', 'IRL Lens observation worksheet', 'Bridge Quest prompts'].map(item => (
                    <li key={item} className="flex items-start gap-2" style={{ color: '#4a5a52', fontSize: 12 }}>
                      <span style={{ color: '#2d7a47', flexShrink: 0 }}>·</span>{item}
                    </li>
                  ))}
                </ul>
                <a
                  href="mailto:contact@haalproject.org?subject=Educator Curriculum Kit Request"
                  className="font-mono uppercase tracking-widest px-6 py-3 inline-block transition-all duration-200"
                  style={{ border: '1px solid rgba(45,122,71,0.4)', color: '#2d7a47', fontSize: 10, letterSpacing: '0.15em' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(45,122,71,0.08)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
                >
                  Request for Classroom →
                </a>
              </div>
            </div>
          </div>

          {/* Prison / Justice-Involved Kit callout */}
          <div className="rounded-xl p-6" style={{ background: 'rgba(90,90,138,0.08)', border: '1px solid rgba(90,90,138,0.2)' }}>
            <div className="flex items-start gap-4">
              <span style={{ color: '#8a8ab8', fontSize: 22, flexShrink: 0 }}>⌘</span>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(138,138,184,0.6)' }}>JUSTICE-INVOLVED SETTINGS</p>
                <h4 className="font-mono uppercase tracking-wide mb-2" style={{ color: '#8a8ab8', fontSize: 12 }}>Prison & Re-Entry Caretaker Kit</h4>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.65, marginBottom: 12 }}>
                  A specialized kit for facilitators working in correctional settings and re-entry programs. Includes the IRL Lens adapted for constrained environments, Amanah-framing for the re-entry journey, and MRG justice-involved resource cards.
                </p>
                <a
                  href="mailto:contact@haalproject.org?subject=Prison & Re-Entry Caretaker Kit Request"
                  className="font-mono text-[9px] uppercase tracking-widest transition-all"
                  style={{ color: 'rgba(138,138,184,0.7)', borderBottom: '1px solid rgba(138,138,184,0.3)' }}
                >
                  Request this kit →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── RESONANCE PATHWAY VIZ ──────────────────────────────────────────────────── */}
      <section className="py-20 px-6" style={{ background: '#060d12' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>مسارات الرنين · SHARED PATHWAYS</p>
            <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>RESONANCE PATHWAYS</h2>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, fontStyle: 'italic', maxWidth: 560, margin: '0 auto' }}>
              These are the paths most traveled by caretakers across the platform — shared routes of recognition.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {SHARED_RESONANCE_PATHWAYS.map(pathway => (
              <button key={pathway.id} onClick={() => setActivePathway(activePathway === pathway.id ? '' : pathway.id)} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all duration-200" style={{ background: activePathway === pathway.id ? `${pathway.color}12` : 'transparent', border: `1px solid ${activePathway === pathway.id ? pathway.color + '50' : 'rgba(255,255,255,0.07)'}`, color: activePathway === pathway.id ? pathway.color : '#4a5a52' }}>
                {pathway.icon} {pathway.title}
              </button>
            ))}
          </div>
          {activePathway && (() => {
            const pathway = SHARED_RESONANCE_PATHWAYS.find(p => p.id === activePathway)!;
            return (
              <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${pathway.color}20` }}>
                <div className="px-6 py-5" style={{ background: `${pathway.color}08`, borderBottom: `1px solid ${pathway.color}15` }}>
                  <div className="flex items-center gap-3 mb-2">
                    <span style={{ color: pathway.color, fontSize: 20 }}>{pathway.icon}</span>
                    <div>
                      <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${pathway.color}80`, direction: 'rtl' }}>{pathway.arabic}</p>
                      <h3 className="font-mono uppercase tracking-widest" style={{ color: pathway.color, fontSize: 12 }}>{pathway.title}</h3>
                    </div>
                    <span className="ml-auto font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded" style={{ background: `${pathway.color}10`, border: `1px solid ${pathway.color}20`, color: pathway.color }}>
                      {pathway.count} caretakers
                    </span>
                  </div>
                  <p style={{ color: '#4a5a52', fontSize: 13, lineHeight: 1.6 }}>{pathway.description}</p>
                </div>
                <div className="px-6 py-4 flex flex-wrap gap-2">
                  {pathway.nodes.map((node, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: `${pathway.color}10`, border: `1px solid ${pathway.color}25`, color: pathway.color }}>{node}</span>
                      {i < pathway.nodes.length - 1 && <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10 }}>→</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ─── TEMPLATE GENERATOR ────────────────────────────────────────────────────── */}
      <section className="py-20 px-6" style={{ background: '#0a1a12' }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>BISMILLAH · بِسْمِ اللَّهِ</p>
            <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>TEMPLATE GENERATOR · مولّد القوالب</h2>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, fontStyle: 'italic', maxWidth: 480, margin: '0 auto' }}>
              Generate phase-specific email, social, and in-person copy — rooted in Sacred Language vocabulary. Every template uses Islah, Amanah, Fisabilillah in context, not as jargon.
            </p>
          </div>
          <div className="rounded-xl p-8" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <TemplateGenerator />
          </div>
        </div>
      </section>

      {/* ─── COMMUNITY SIGNUP ────────────────────────────────────────────────────── */}
      <section ref={signupRef} className="py-20 px-6" style={{ background: '#0d1f16' }}>
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>JOIN THE CARETAKERS · انضم إلى القائمين</h2>
            <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic' }}>Get updates as the platform grows. Share ideas. Help shape what comes next.</p>
          </div>
          {signupSubmitted ? (
            <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)' }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#c9a84c', marginBottom: 8 }}>الحمد لله</p>
              <h3 className="font-mono uppercase tracking-widest mb-4" style={{ color: '#d4c5a9', fontSize: 14 }}>Alhamdulilah. You&apos;re in.</h3>
              <p className="mb-8" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7 }}>We&apos;ll be in touch as the platform grows. In the meantime, explore:</p>
              <div className="flex flex-col gap-3">
                <Link href="/inner-reality-lens" className="font-mono text-xs tracking-widest py-3 px-6 text-center" style={{ border: '1px solid rgba(45,138,135,0.4)', color: '#2d8a87' }}>IRL Lens →</Link>
                <Link href="/open-letter" className="font-mono text-xs tracking-widest py-3 px-6 text-center" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>Open Letter →</Link>
                <Link href="/names-reference" className="font-mono text-xs tracking-widest py-3 px-6 text-center" style={{ border: '1px solid rgba(212,197,169,0.2)', color: '#d4c5a9' }}>Names Reference →</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="rounded-xl p-8 flex flex-col gap-4" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <input type="text" placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-3 font-mono text-sm outline-none" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }} />
              <input type="email" placeholder="your@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="w-full px-4 py-3 font-mono text-sm outline-none" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }} />
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full px-4 py-3 font-mono text-sm outline-none" style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(201,168,76,0.15)', color: form.role ? '#d4c5a9' : 'rgba(212,197,169,0.4)', borderRadius: 4 }}>
                <option value="">Your role (optional)</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input type="text" placeholder="Your city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="w-full px-4 py-3 font-mono text-sm outline-none" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }} />
              <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="w-full px-4 py-3 font-mono text-sm outline-none" style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(201,168,76,0.15)', color: form.source ? '#d4c5a9' : 'rgba(212,197,169,0.4)', borderRadius: 4 }}>
                <option value="">How did you find us? (optional)</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {signupError && <p className="font-mono text-xs" style={{ color: '#e07070' }}>{signupError}</p>}
              <button type="submit" disabled={signupLoading} className="font-mono uppercase tracking-widest py-4 transition-all duration-300" style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: signupLoading ? 'wait' : 'pointer', borderRadius: 4 }}>
                {signupLoading ? 'Joining...' : 'Join →'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes goldShimmer {
          0%, 100% { opacity: 1; text-shadow: 0 0 20px rgba(201,168,76,0.3); }
          50% { opacity: 0.85; text-shadow: 0 0 40px rgba(201,168,76,0.6); }
        }
      `}</style>
    </div>
  );
}
